// The draft_items behavior's job and contract (Scribe). Pure, so the agent
// definitions can name the job without importing the browser's model client.
// Scribe runs only when a person uses it (the Scribe page): it drafts items
// from their text in the session, and the person creates the ones they keep.
// The prompt and parse live in src/lib/llmScribeService.ts — there is no
// server host for this behavior, so there is no second copy.

import { buildInstructionStack, type AgentCard } from './instructionStackCore.ts';
import { LANGUAGE_NAMES } from './estimateValueCore.ts';

/** The contract the model works to — the appendix of the instruction stack. */
export const DRAFT_ITEMS_SYSTEM_PROMPT =
  'You are Scribe, a strategic intake agent. Parse text into structured work items. Respond with valid JSON only.';

export const DRAFT_ITEMS_JOB = {
  capabilityId: 'scribe',
  role: 'Turn what a person typed or said into draft items, for that person to keep, edit or skip.',
  goal:
    'Let people capture work in their own words — a meeting note, a voice memo — and get well-formed ' +
    'items without filling in a form for each one.',
  outputContract:
    'Per distinct piece of work in the text: one draft item — title, description, category, altitude, ' +
    'confidence, rationale and the names it mentions. Nothing that is not in the text.',
  skillStack: ['Item extraction', 'Category assignment', 'Altitude scoping', 'Rationale compression'],
  reads: ['the text the person typed or dictated', 'reference documents an administrator added'],
  proposes: ['draft items the person keeps, edits or skips — nothing is created until they do'],
  modelDefaults: { temperature: 0.3, maxTokens: 2000 },
} as const;

export function draftItemsAgentCard(): AgentCard {
  return {
    capabilityId: DRAFT_ITEMS_JOB.capabilityId,
    role: DRAFT_ITEMS_JOB.role,
    goal: DRAFT_ITEMS_JOB.goal,
    permissions: { read: [...DRAFT_ITEMS_JOB.reads], propose: [...DRAFT_ITEMS_JOB.proposes] },
    memory: 'turn',
    skillStack: [...DRAFT_ITEMS_JOB.skillStack],
    outputContract: DRAFT_ITEMS_JOB.outputContract,
  };
}

/** The workspace text is a subordinate overlay — it specializes, it never replaces the contract (§2 H). */
export function buildDraftItemsSystemPrompt(params: { overlay?: string | null; language?: string | null }): { systemPrompt: string } {
  const overlay = params.overlay?.trim();
  const effectiveOverlay = overlay && overlay !== DRAFT_ITEMS_SYSTEM_PROMPT ? overlay : null;
  const language = params.language && LANGUAGE_NAMES[params.language]
    ? `Write titles, descriptions and rationales in ${LANGUAGE_NAMES[params.language]}. Keep categories and JSON keys exactly as given.`
    : null;
  const { systemPrompt } = buildInstructionStack({
    card: draftItemsAgentCard(),
    capabilityAppendix: [DRAFT_ITEMS_SYSTEM_PROMPT, language].filter(Boolean).join('\n'),
    workspaceOverlay: effectiveOverlay,
  });
  return { systemPrompt };
}
